import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { testimonialsApi, type Testimonial } from '@/lib/api';

export const useTestimonials = () => {
  return useQuery({
    queryKey: ['testimonials'],
    queryFn: testimonialsApi.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

export const useTestimonial = (id: string) => {
  return useQuery({
    queryKey: ['testimonials', id],
    queryFn: () => testimonialsApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

export const useCreateTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (testimonial: Omit<Testimonial, 'id'>) => testimonialsApi.create(testimonial),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
};

export const useUpdateTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Testimonial> }) =>
      testimonialsApi.update(id, updates),
    onSuccess: (updatedTestimonial) => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['testimonials', updatedTestimonial.id] });
    },
  });
};

export const useDeleteTestimonial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => testimonialsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
};
